import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import ReactDOM from 'react-dom'
import { useStateValue } from '@State'
import { saveAnswer } from '../../api'
import { FeedbackDisplay } from './utils/FeedbackDisplay'
import { TabSplitLayout } from './utils/TabSplitLayout'
import { SaveIndicator } from './utils/SaveIndicator'
import {
  FEEDBACK_LABELS, getOptionFeedbackStatus,
  OPTION_FEEDBACK_STYLE, FEEDBACK_BADGE,
} from './utils/feedbackStyles'

const PHRASE_BASE = {
  padding: '0.25rem 0.5rem', display: 'inline-block', margin: '0.1rem',
  border: '2px dashed #9ca3af', backgroundColor: '#fff', cursor: 'pointer',
  borderRadius: '6px', userSelect: 'none',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
}

const PHRASE_SELECTED = {
  ...PHRASE_BASE, backgroundColor: '#00498D', color: '#fff',
  borderStyle: 'solid', borderColor: '#00498D',
}

const PHRASE_SAVING = { ...PHRASE_BASE, cursor: 'wait', opacity: 0.7 }
const PHRASE_DISABLED = { ...PHRASE_BASE, opacity: 0.45, cursor: 'default' }

const QUESTION_TEXT = `
.ngn-highlight-text * {
  text-align: start;
}
.ngn-highlight-prompt * {
  text-align: start;
}
.ngn-highlight-prompt span,
.ngn-highlight-prompt p,
{
  display: inline-block
}

`

const MAX_REACHED_STYLE = {
  color: '#856404', backgroundColor: '#fff3cd', border: '1px solid #ffeeba',
  borderRadius: '6px', padding: '0.35rem 0.65rem', marginBottom: '0.5rem',
  fontSize: '0.85rem', fontWeight: 400,
}

const PHRASE_FEEDBACK = {
  correct: { ...PHRASE_BASE, borderStyle: 'solid', ...OPTION_FEEDBACK_STYLE.correct, color: '#1a1a1a', cursor: 'default' },
  incorrect: { ...PHRASE_BASE, borderStyle: 'solid', ...OPTION_FEEDBACK_STYLE.incorrect, color: '#1a1a1a', cursor: 'default' },
  missed: { ...PHRASE_BASE, borderStyle: 'solid', ...OPTION_FEEDBACK_STYLE.missed, color: '#1a1a1a', cursor: 'default' },
  neutral: { ...PHRASE_BASE, ...OPTION_FEEDBACK_STYLE.neutral, cursor: 'default' },
}

function buildPromptHtml(prompt) {
  return prompt.replace(/\{(\d+)\}/g, (_, n) =>
    `<span data-phrase-index="${n}"></span>`
  )
}

export const NGNHighlightQuestion = ({ question, renderTabs = true }) => {
  const [state, dispatch] = useStateValue()
  const { coursenum, activitySubmissionId, savingGuids, readonly } = state
  const isSaving = savingGuids.has(question.guid)

  const initialSelected = useMemo(() => {
    if (!question.answer) return []
    try {
      const parsed = JSON.parse(question.answer)
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }, [question.answer])

  const [selectedGuids, setSelectedGuids] = useState(initialSelected)
  const containerRef = useRef(null)
  const [portalContainers, setPortalContainers] = useState([])

  useEffect(() => {
    if (!containerRef.current) return
    const spans = containerRef.current.querySelectorAll('[data-phrase-index]')
    setPortalContainers(Array.from(spans))

    containerRef.current.querySelectorAll('table').forEach(el => {
      el.classList.add('table')
    })

  }, [])

  useEffect(() => {
    const selected = JSON.parse(question.answer || '[]').length
    const { minSelections, maxSelections } = question.data
    dispatch({
      type: 'SET_FULLY_ANSWERED', guid: question.guid,
      value: selected >= minSelections && selected <= maxSelections
    })
  }, [question.answer])

  const { maxSelections } = question.data
  const atMax = maxSelections && selectedGuids.length >= maxSelections

  const togglePhrase = useCallback(async (phraseGuid) => {
    if (readonly || isSaving) return

    const isSelected = selectedGuids.includes(phraseGuid)
    if (!isSelected && atMax) return

    const updated = isSelected
      ? selectedGuids.filter(g => g !== phraseGuid)
      : [...selectedGuids, phraseGuid]

    setSelectedGuids(updated)

    const answerJson = JSON.stringify(updated)
    dispatch({ type: 'SET_ANSWER', guid: question.guid, answer: answerJson })
    dispatch({ type: 'SAVE_START', guid: question.guid })

    try {
      const response = await saveAnswer(coursenum, activitySubmissionId, question.guid, answerJson)
      dispatch({ type: 'SAVE_SUCCESS', guid: question.guid, payload: response.data })
    } catch (e) {
      dispatch({ type: 'SAVE_ERROR', guid: question.guid })
    }
  }, [selectedGuids, readonly, isSaving, atMax, coursenum, activitySubmissionId, question.guid, dispatch])

  const clearHighlights = useCallback(async () => {
    if (readonly || isSaving) return

    setSelectedGuids([])

    const answerJson = JSON.stringify([])
    dispatch({ type: 'SET_ANSWER', guid: question.guid, answer: answerJson })
    dispatch({ type: 'SAVE_START', guid: question.guid })

    try {
      const response = await saveAnswer(coursenum, activitySubmissionId, question.guid, answerJson)
      dispatch({ type: 'SAVE_SUCCESS', guid: question.guid, payload: response.data })
    } catch (e) {
      dispatch({ type: 'SAVE_ERROR', guid: question.guid })
    }
  }, [readonly, isSaving, coursenum, activitySubmissionId, question.guid, dispatch])

  const handleKeyDown = useCallback((e, phraseGuid) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      togglePhrase(phraseGuid)
    }
  }, [togglePhrase])

  const { showFeedback } = state
  const { showAnswer } = state.feedbackSettings
  const correctSet = useMemo(() => {
    if (!showFeedback || !showAnswer || !question.correctAnswer) return null
    return new Set(question.correctAnswer)
  }, [showFeedback, showAnswer, question.correctAnswer])

  return (
    <>
      <FeedbackDisplay question={question} />
      <div className="position-relative">
        <SaveIndicator questionGuid={question.guid} />
      </div>
      <style>{QUESTION_TEXT}</style>
      <TabSplitLayout question={question} renderTabs={renderTabs}>
        <div className="d-block text-align-start w-100">
          <div
            role="heading"
            aria-level={3}
            className="mb-2 text-left ngn-highlight-text"
            dangerouslySetInnerHTML={{ __html: question.text }}
          />

          <div
            ref={containerRef}
            dangerouslySetInnerHTML={{ __html: buildPromptHtml(question.data.prompt || '') }}
            className='ngn-highlight-prompt'
          />
          {portalContainers.map(span => {
            const phraseIndex = parseInt(span.dataset.phraseIndex, 10)
            const phrase = question.data.phrases[phraseIndex]
            if (!phrase) return null

            const isSelected = selectedGuids.includes(phrase.guid)

            if (correctSet) {
              const isCorrectPhrase = correctSet.has(phrase.guid)
              const status = getOptionFeedbackStatus(isSelected, isCorrectPhrase)
              const badge = status !== 'neutral'
                ? <span style={FEEDBACK_BADGE[status]}>{FEEDBACK_LABELS[status]}</span>
                : null
              return ReactDOM.createPortal(
                <span style={PHRASE_FEEDBACK[status]}>{phrase.text}{badge}</span>,
                span
              )
            }

            const isDisabled = readonly || (!isSelected && atMax)
            const btnStyle = isSaving ? PHRASE_SAVING : isDisabled && !readonly ? PHRASE_DISABLED : isSelected ? PHRASE_SELECTED : PHRASE_BASE
            return ReactDOM.createPortal(
              <button
                type="button"
                tabIndex={0}
                style={btnStyle}
                aria-pressed={isSelected}
                disabled={isDisabled}
                onClick={() => togglePhrase(phrase.guid)}
                onKeyDown={e => handleKeyDown(e, phrase.guid)}
                dangerouslySetInnerHTML={{ __html: phrase.text }}
              >
              </button>,
              span
            )
          })}
          {!readonly && (
            atMax
              ? <div style={MAX_REACHED_STYLE}>Maximum selections reached ({maxSelections}). Deselect a highlight to choose a different one.</div>
              : <div style={{ ...MAX_REACHED_STYLE, visibility: 'hidden' }}>&nbsp;</div>
          )}
          {!readonly && (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary mt-2"
              onClick={clearHighlights}
              disabled={isSaving}
            >
              Clear Highlights
            </button>
          )}
        </div >
      </TabSplitLayout>
    </>
  )
}
